import { useEffect, useState } from "react";
import type { GraduationIntake } from "./handler-mission";
import { useVoiceAnswer } from "./use-voice-answer";
import { Mic, Square } from "lucide-react";
import "./handler-starting-clues.css";

const TIME_OPTIONS: { value: GraduationIntake["time"]; label: string }[] = [
  { value: "ten", label: "Next ten minutes" },
  { value: "hour", label: "Next hour" },
  { value: "five", label: "Next five hours" },
  { value: "day", label: "Next twenty-four hours" },
];

const PLACE_OPTIONS: { value: GraduationIntake["place"]; label: string }[] = [
  { value: "home", label: "At home" },
  { value: "work", label: "At work" },
  { value: "out", label: "Out and about" },
  { value: "friends", label: "With friends" },
  { value: "other", label: "Somewhere else" },
  { value: "unsure", label: "Not sure yet" },
];

const PERSON_OPTIONS: { value: GraduationIntake["person"]; label: string }[] = [
  { value: "partner", label: "My partner" },
  { value: "family", label: "Family" },
  { value: "friend", label: "A friend" },
  { value: "work", label: "Someone at work" },
  { value: "neighbor", label: "A neighbor" },
  { value: "stranger", label: "A complete stranger" },
  { value: "other", label: "Someone else" },
  { value: "unsure", label: "Not sure yet" },
];

function loadDraft(storageKey: string): Partial<GraduationIntake> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveDraft(storageKey: string, draft: Partial<GraduationIntake>) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(draft));
  } catch {
    /* storage unavailable */
  }
}

export function HandlerGraduationIntake({
  name,
  onComplete,
  initial,
  storageKey,
  submitting = false,
}: {
  name: string;
  storageKey: string;
  submitting?: boolean;
  onComplete: (grad: GraduationIntake) => void;
  initial?: Partial<GraduationIntake>;
}) {
  const [restored] = useState(() => initial ?? loadDraft(storageKey));
  const [page, setPage] = useState(() => {
    if (!restored) return 0;
    if (restored.person) return 3;
    if (restored.place) return 2;
    if (restored.time) return 1;
    return 0;
  });
  const [time, setTime] = useState<GraduationIntake["time"] | "">(
    () => (restored?.time as GraduationIntake["time"]) || "",
  );
  const [place, setPlace] = useState<GraduationIntake["place"] | "">(
    () => (restored?.place as GraduationIntake["place"]) || "",
  );
  const [person, setPerson] = useState<GraduationIntake["person"] | "">(
    () => (restored?.person as GraduationIntake["person"]) || "",
  );
  const [personName, setPersonName] = useState(() => restored?.name || "");
  const [idea, setIdea] = useState(() => restored?.idea || "");
  const voice = useVoiceAnswer((text: string) =>
    setIdea((prev) => (prev ? prev + " " + text : text)),
  );
  const recording = voice.status === "recording";

  useEffect(() => {
    const draft: Partial<GraduationIntake> = {};
    if (time) draft.time = time as GraduationIntake["time"];
    if (place) draft.place = place as GraduationIntake["place"];
    if (person) draft.person = person as GraduationIntake["person"];
    if (personName) draft.name = personName;
    if (idea) draft.idea = idea;
    saveDraft(storageKey, draft);
  }, [time, place, person, personName, idea, storageKey]);

  const titles = [
    "When will you do this?",
    "Where will you be?",
    "Who is this for?",
    "What's your idea?",
  ];

  const submit = (help: boolean) => {
    if (!time || !place || !person || submitting) return;
    if (!help && !idea.trim()) return;
    onComplete({
      time: time as GraduationIntake["time"],
      place: place as GraduationIntake["place"],
      person: person as GraduationIntake["person"],
      name: personName.trim(),
      idea: help ? "" : idea.trim(),
      needHelp: help,
    });
  };

  return (
    <div className="handler-starting-clues">
      <div className="handler-guided-history">
        <div className="handler-message-row">
          <div className="handler-bubble">
            <small>{name}</small>
            <p>
              This is your graduation mission. You design it, I help you shape
              it. Let me know the basics, and we will figure out the rest
              together.
            </p>
          </div>
        </div>
      </div>
      <div className="handler-picker-card">
        <p className="handler-clue-progress">
          Graduation mission &middot; {page + 1} of 4
        </p>
        <h2>{titles[page]}</h2>
        <div
          className="handler-clue-options"
          role="group"
          aria-label={titles[page]}
        >
          {page === 0 &&
            TIME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={time === opt.value}
                onClick={() => {
                  setTime(opt.value);
                  setPage(1);
                }}
              >
                <span>{opt.label}</span>
              </button>
            ))}
          {page === 1 &&
            PLACE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={place === opt.value}
                onClick={() => {
                  setPlace(opt.value);
                  setPage(2);
                }}
              >
                <span>{opt.label}</span>
              </button>
            ))}
          {page === 2 &&
            PERSON_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                aria-pressed={person === opt.value}
                onClick={() => {
                  setPerson(opt.value);
                  setPage(3);
                }}
              >
                <span>{opt.label}</span>
              </button>
            ))}
          {page === 3 && (
            <>
              {person !== "stranger" && person !== "unsure" && (
                <div className="handler-clue-detail">
                  <label htmlFor="grad-name">Their name (optional)</label>
                  <input
                    id="grad-name"
                    type="text"
                    value={personName}
                    maxLength={50}
                    placeholder="First name or nickname"
                    onChange={(e) => setPersonName(e.target.value)}
                  />
                </div>
              )}
              <div className="handler-clue-detail">
                <label htmlFor="grad-idea">
                  Your idea (optional — type or use voice)
                </label>
                <textarea
                  id="grad-idea"
                  value={idea}
                  maxLength={2000}
                  placeholder="What do you have in mind?"
                  rows={3}
                  onChange={(e) => setIdea(e.target.value)}
                />
                <button
                  type="button"
                  className="handler-quiet"
                  aria-label={
                    recording ? "Stop recording" : "Use voice for your idea"
                  }
                  disabled={submitting || (voice.busy && !recording)}
                  onClick={() =>
                    recording ? voice.stop() : void voice.start()
                  }
                >
                  {recording ? (
                    <Square size={16} aria-hidden="true" />
                  ) : (
                    <Mic size={16} aria-hidden="true" />
                  )}
                  <span>
                    {recording
                      ? "Stop"
                      : voice.status === "transcribing"
                        ? "Transcribing…"
                        : "Speak"}
                  </span>
                </button>
                {voice.error && <p role="alert">{voice.error}</p>}
              </div>
              <button
                type="button"
                className="handler-continue-mission"
                disabled={voice.busy || submitting}
                onClick={() => submit(false)}
              >
                {idea.trim() ? "Let's go" : "I have an idea"}
              </button>
              <button
                type="button"
                className="handler-quiet"
                disabled={voice.busy || submitting}
                onClick={() => submit(true)}
              >
                Help me brainstorm
              </button>
            </>
          )}
        </div>
      </div>
      {page > 0 && (
        <button
          className="handler-quiet"
          type="button"
          onClick={() => setPage(page - 1)}
        >
          Back
        </button>
      )}
    </div>
  );
}
